import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Button, Flex, useDisclosure, useStyleConfig, Box} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
//import {useForm} from 'react-hook-form'
import Field from '../field'
import Feedback from '../feedback'
import useGetVatValidation from '../../custom-api/hooks/use-get-vat-validation'
import {useBooleanCustomPreference, useCustomPreference} from '../../hooks/use-custom-preference'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {useCheckoutActions} from '../../custom-hooks/use-checkout-actions'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'

const VatValidator = ({form, fields}) => {
    const [vatData, setVatData] = useState(null)
    const enableVatSense = useBooleanCustomPreference('enableVATSense')
    const {formatMessage} = useIntl()
    // const {handleSubmit, control, setValue} = form // useForm()
    // const [isInputEmpty, setIsInputEmpty] = useState(false)
    const {data: basket} = useCurrentBasket()
    const {updateBasketWithVatVerified} = useCheckoutActions()

    const [isNameDisabled, setIsNameDisabled] = useState(true)
    const [vatValid, setVatValid] = useState(false)
    const [warningMessage, setWarningMessage] = useState(null)
    const {isOpen, onOpen, onClose} = useDisclosure()
    const styles = useStyleConfig('VatValidator')
    const {data, isLoading, isError, isFetching, isSuccess} = useGetVatValidation(vatData)
    var editB2BCompanyName = useBooleanCustomPreference('editB2BCompanyName')
    const smallB2BAdditionalInputFieldConfig = useCustomPreference('smallB2BAdditionalInputField')

    if (!enableVatSense) {
        editB2BCompanyName = true
    }

    // Save the previous value of the vatNumber field
    const prevVatNumberRef = useRef('')

    // Initialize prevVatNumberRef with the current value when the component mounts
    useEffect(() => {
        const initialValue = form.getValues('c_vatNumber')
        prevVatNumberRef.current = initialValue || ''
    }, [])

    // Save the current value of the VAT number when it changes for comparison
    const watchVatNumber = form.watch('c_vatNumber')

    // Log the data returned by the hook
    useEffect(() => {
        if (isSuccess) {
            form.clearErrors('c_vatNumber')
            if (data?.code === 200) {
                if (!editB2BCompanyName && data.valid && data.c_companyName) {
                    form.setValue('c_companyName', data.c_companyName)
                    setIsNameDisabled(true)
                    updateBasketWithVatVerified(basket, true)
                    setVatValid(true)
                    onClose()
                } else if (data.valid) {
                    setVatValid(true)
                    form.setValue('c_companyName', '')
                    updateBasketWithVatVerified(basket, false)
                    setIsNameDisabled(false)
                }
            } else {
                setIsNameDisabled(false)
                setVatValid(false)
                updateBasketWithVatVerified(basket, null)
                setWarningMessage(errorMessages[400])
                onOpen()
            }
        }
        if (data && !data.valid) {
            form.setError('c_vatNumber', {
                type: 'manual',
                message: formatMessage({
                    id: 'vat_validator.vat_input_error.vat_number_invalid',
                    defaultMessage: `Please insert your VAT number`,
                }),
            })
            // in case of request error clean any possible vat verification value from the basket
            updateBasketWithVatVerified(basket, null)
        }
        if (isError) {
            setIsNameDisabled(true)
            setVatValid(false)
            form.BoxsetValue('c_companyName', '')
        }
    }, [data, isLoading, isSuccess])

    useEffect(() => {
        form.setValue('c_vatNumberVerified', vatValid)
    }, [vatValid])

    //Message for the warning , its big
    const errorMessages = {
        400: formatMessage({
            id: 'vat_validator_warning_message.message',
            defaultMessage: `Your VAT number code appears to be incorrect or not related to the VIES circuit 
                (be sure to include the initial letters, for example IT*******, DE******, etc.). 
                We inform you that it is not possible to proceed with orders for entities that are not part of the VIES circuit. 
                We invite you to verify your VAT number again and, in case of doubts or errors, contact us for assistance.`,
        }),
        // 401: 'No valid API key provided.',
        // 402: 'The parameters were valid but the request failed.',
        // 404: "The requested resource doesn't exist",
        // 409: 'The request conflicts with another request (perhaps due to using the same idempotent key).',
        // 412: 'The EU VIES service is temporarily unavailable. Please try again later.',
        // 429: 'Too many requests hit the API too quickly. We recommend an exponential backoff of your requests.',
        // 500: 'Server Error',
    }

    //Handles the data manipulation on submit
    const handleVatSubmit = async () => {
        //setIsNameDisabled(true)
        //setVatValid(false)
        //setValue('CompanyName', '')
        const isValid = await form.trigger('c_vatNumber')
        if (!isValid) {
            // If not valid, do not proceed
            return
        }
        const VatNumber = form.getValues('c_vatNumber')

        if (!VatNumber) {
            form.setError('c_vatNumber', {
                type: 'manual',
                message: formatMessage({
                    id: 'vat_validator.vat_input_error.vat_number',
                    defaultMessage: `Please insert your VAT number`,
                }),
            })
            // setIsInputEmpty(true)
            return
        }
        form.clearErrors('c_vatNumber')
        setVatData({VatNumber})
        //save the ref only when the vat-sense is called
        //avoid to clean the basket isvatApproved in case of revalidation of the same vat number
        prevVatNumberRef.current = watchVatNumber
        // setIsInputEmpty(false)
        onClose()
    }

    return (
        <Flex {...styles?.outerContainer} id="vat-outer-container">
            <Flex {...styles?.formContainer} id="vat-form-container">
                <Flex {...styles.flexContainer} id="vat-flex-container">
                    <Flex {...styles?.vatInputContainer} id="vat-input-container">
                        <Field
                            {...fields.vatNumber}
                            inputProps={{
                                isSuccess: !isLoading && vatValid,
                                onBlur: () => {
                                    // get currentVatValue from hook form
                                    const currentVatValue = form.getValues('c_vatNumber')

                                    // be sure to reset the validation state when the actual vat number has been changed
                                    // after a previous successful validation
                                    if (prevVatNumberRef.current !== currentVatValue) {
                                        updateBasketWithVatVerified(basket, null)
                                    }
                                },
                            }}
                        />
                        {enableVatSense && (
                            <Button
                                variant="primary"
                                onClick={handleVatSubmit}
                                data-testid="vat-validator-button"
                            >
                                {formatMessage({
                                    defaultMessage: 'Verify VAT',
                                    id: 'vat_validator.vat_api_call_button.text',
                                })}
                            </Button>
                        )}
                    </Flex>
                    {isOpen ? (
                        <Feedback
                            data-testid="vat-warning-messsage"
                            status="error"
                            variant="vatWarning"
                            message={warningMessage}
                            onClose={onClose}
                        />
                    ) : (
                        <></>
                    )}
                    <Box {...{display: 'none'}}>
                        <Field {...fields.vatConfirmed} isDisabled={true} />
                    </Box>
                </Flex>

                <Flex {...styles?.inputContainer}>
                    <Field
                        {...fields.companyName}
                        inputProps={{
                            isDisabled: !editB2BCompanyName,
                            isSuccess: !isLoading && vatValid && isNameDisabled,
                        }}
                    />
                    {smallB2BAdditionalInputFieldConfig &&
                        smallB2BAdditionalInputFieldConfig?.active === 'true' && (
                            <Field
                                {...fields.sdi}
                                inputProps={{
                                    tooltip: 'tooltip message',
                                    tooltipVariant: 'top-left',
                                    tooltipOpensOn: 'hover',
                                    hideSuccessIcon: true,
                                }}
                            />
                        )}
                </Flex>
            </Flex>
            {isFetching && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}
        </Flex>
    )
}

VatValidator.propTypes = {
    form: PropTypes.object,
    fields: PropTypes.object,
}

export default VatValidator
